<script setup lang="ts">
import { ref, computed } from 'vue';
import { confirmations } from '../messages';

interface Props {
    isStreaming: boolean;
    isFailure: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    sendMessage: [content: string];
    deleteConversation: [];
}>();

const disabled = computed(() => props.isStreaming || props.isFailure);

const placeholderMessage = computed(() => {
    if (props.isFailure) {
        return 'Ein Fehler ist aufgetreten.';
    }
    if (props.isStreaming) {
        return 'Ella antwortet gerade';
    }
    return 'Frag Ella …';
});

const messageInput = ref('');
const inputElement = ref<HTMLInputElement | null>(null);

const handleSubmit = () => {
    const content = messageInput.value.trim();
    if (!content) return;

    emit('sendMessage', content);
    messageInput.value = '';
};

const focus = () => {
    inputElement.value?.focus();
};

const handleDeleteConversation = () => {
    if (window.confirm(confirmations.delete)) {
        emit('deleteConversation');
    }
};

const handleNewConversation = () => {
    if (window.confirm(confirmations.new)) {
        window.location.reload();
    }
};

defineExpose({ focus });
</script>

<template>
    <div class="chat__interface">
        <div class="container">
            <form @submit.prevent="handleSubmit">
                <fieldset :disabled>
                    <legend class="visually-hidden">Ella eine Nachricht schicken</legend>
                    <div class="chat__input-group">
                        <label for="message" class="visually-hidden">Nachricht eingeben</label>
                        <input
                            id="message"
                            ref="inputElement"
                            v-model="messageInput"
                            type="text"
                            name="message"
                            class="chat__input"
                            :placeholder="placeholderMessage"
                            autocomplete="off"
                        />

                        <button type="submit" class="chat__submit">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                class="chat__submit-icon"
                                width="20"
                                height="20"
                                aria-hidden="true"
                            >
                                <path
                                    fill="currentColor"
                                    d="M498.1 5.6c10.1 7 15.4 19.1 13.5 31.2l-64 416c-1.5 9.7-7.4 18.2-16 23s-18.9 5.4-28 1.6L284 427.7l-68.5 74.1c-8.9 9.7-22.9 12.9-35.2 8.1S160 493.2 160 480l0-83.6c0-4 1.5-7.8 4.2-10.8L331.8 202.8c5.8-6.3 5.6-16-.4-22s-15.7-6.4-22-.7L106 360.8 17.7 316.6C7.1 311.3 .3 300.7 0 288.9s5.9-22.8 16.1-28.7l448-256c10.7-6.1 23.9-5.5 34 1.4z"
                                />
                            </svg>
                            <span class="visually-hidden">Nachricht senden</span>
                        </button>
                    </div>
                </fieldset>
            </form>

            <div class="chat__toolbar">
                <button :disabled="isStreaming" class="chat__toolbar-button btn btn-link" @click="handleDeleteConversation">
                    Zustimmung widerrufen und Chat löschen
                </button>
                <button :disabled="isStreaming" class="chat__toolbar-button btn btn-link" @click="handleNewConversation">
                    Neuen Chat starten
                </button>
            </div>
        </div>
    </div>
</template>
